import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../utils/constants';

interface RedditAnalyticsData {
  totalPostsFound: number;
  highPriorityPosts: number;
  recentPosts: number;
  highEngagementPosts: number;
  averageRelevanceScore: number;
  topSubreddits: Array<{ subreddit: string; count: number }>;
  lastUpdated: string;
}

const RedditAnalytics: React.FC = () => {
  const [analytics, setAnalytics] = useState<RedditAnalyticsData | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/reddit/analytics`);
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Error fetching Reddit analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Reddit Marketing Analytics</h3>
        <p className="text-gray-600">No analytics data available</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Reddit Marketing Analytics</h3>
        <button
          onClick={fetchAnalytics}
          className="text-sm text-blue-600 hover:text-blue-800"
        >
          Refresh
        </button>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-900">{analytics.totalPostsFound}</div>
          <div className="text-sm text-gray-600">Total Posts</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-red-600">{analytics.highPriorityPosts}</div>
          <div className="text-sm text-gray-600">High Priority</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">{analytics.recentPosts}</div>
          <div className="text-sm text-gray-600">Recent (24h)</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600">{analytics.highEngagementPosts}</div>
          <div className="text-sm text-gray-600">High Engagement</div>
        </div>
      </div>

      {/* Average Relevance Score */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">Average Relevance Score</span>
          <span className="text-lg font-semibold text-gray-900">
            {analytics.averageRelevanceScore.toFixed(1)}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
          <div
            className="bg-blue-600 h-2 rounded-full"
            style={{ width: `${(analytics.averageRelevanceScore / 15) * 100}%` }}
          ></div>
        </div>
      </div>

      {/* Top Subreddits */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-3">Top Subreddits</h4>
        <div className="space-y-2">
          {analytics.topSubreddits.slice(0, 5).map((item: { subreddit: string; count: number }, index: number) => (
            <div key={item.subreddit} className="flex items-center justify-between">
              <span className="text-sm text-gray-600">r/{item.subreddit}</span>
              <span className="text-sm font-medium text-gray-900">{item.count} posts</span>
            </div>
          ))}
        </div>
      </div>

      {/* Last Updated */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-500">
          Last updated: {new Date(analytics.lastUpdated).toLocaleString()}
        </p>
      </div>
    </div>
  );
};

export default RedditAnalytics; 